import './CompanySettingsSection.css';
import React, { useState, useEffect, useCallback } from 'react';
import { loadCompany, saveCompany } from '../../logic/companySettingsLogic';

export default function CompanySettingsSection({ notify }) {
  const [companyForm, setCompanyForm] = useState({
    company_name:'', domain:'', company_url:'', logo_url:'', favicon_url:'', primary_color:'', timezone:'UTC',
  });
  const [companyLoaded, setCompanyLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const isBusy = key => !!busy[key];
  const withBusy = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const company = await loadCompany();
      setCompanyForm(company);
      setCompanyLoaded(true);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load company settings', true); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const handleSaveCompany = async e => {
    e.preventDefault();
    await withBusy('save-company', async () => {
      try {
        const updated = await saveCompany(companyForm);
        setCompanyForm(updated);
        if (updated.primary_color) document.documentElement.style.setProperty('--primary', updated.primary_color);
        if (updated.company_name)  window.__companyName = updated.company_name;
        notify('Company settings saved successfully!');
      } catch (e) { notify(e.response?.data?.message || 'Failed to save company settings', true); }
    });
  };

  return (
    <div className="fade-up">
      <div className="form-section" style={{maxWidth:'640px'}}>
        <h3 style={{marginBottom:'1.5rem'}}>Company Settings</h3>
        {!companyLoaded && loading
          ? <div className="admin-loading"><div className="spinner"/></div>
          : (
          <form onSubmit={handleSaveCompany}>
            <div className="input-group" style={{marginBottom:'1.25rem'}}>
              <label>Company Name *</label>
              <input className="input-field" placeholder="e.g. Acme Corp" required
                value={companyForm.company_name}
                onChange={e=>setCompanyForm(f=>({...f,company_name:e.target.value}))}
              />
            </div>

            <div className="input-group" style={{marginBottom:'1.25rem'}}>
              <label>Company day timezone (IANA)</label>
              <input className="input-field" placeholder="e.g. Asia/Kolkata, America/New_York, UTC"
                value={companyForm.timezone}
                onChange={e=>setCompanyForm(f=>({...f,timezone:e.target.value}))}
              />
              <span style={{fontSize:'0.78rem',color:'var(--muted)'}}>
                Defines midnight for daily unique question assignment. Must be a valid IANA timezone name.
              </span>
            </div>

            <div className="input-group" style={{marginBottom:'1.25rem'}}>
              <label>Domain(s)</label>
              <input className="input-field" placeholder="e.g. hire.acme.com, localhost:3000"
                value={companyForm.domain}
                onChange={e=>setCompanyForm(f=>({...f,domain:e.target.value}))}
              />
              <span style={{fontSize:'0.78rem',color:'var(--muted)'}}>Comma-separated. Used to auto-apply branding based on the URL.</span>
            </div>

            <div className="input-group" style={{marginBottom:'1.25rem'}}>
              <label>App / Portal URL</label>
              <input className="input-field" placeholder="e.g. https://test.acme.com"
                value={companyForm.company_url}
                onChange={e=>setCompanyForm(f=>({...f,company_url:e.target.value}))}
              />
              <span style={{fontSize:'0.78rem',color:'var(--muted)'}}>Used as the login link in all welcome emails sent to admins and candidates.</span>
            </div>

            <div className="input-group" style={{marginBottom:'1.25rem'}}>
              <label>Primary Brand Color</label>
              <div style={{display:'flex',gap:'0.75rem',alignItems:'center'}}>
                <input type="color" style={{width:'48px',height:'38px',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',padding:'2px',background:'var(--surface)'}}
                  value={companyForm.primary_color || '#2563eb'}
                  onChange={e=>setCompanyForm(f=>({...f,primary_color:e.target.value}))}
                />
                <input className="input-field" placeholder="#2563eb" style={{flex:1,fontFamily:'DM Mono,monospace'}}
                  value={companyForm.primary_color}
                  onChange={e=>setCompanyForm(f=>({...f,primary_color:e.target.value}))}
                />
              </div>
              <div style={{marginTop:'0.5rem',height:'12px',borderRadius:'6px',background:companyForm.primary_color||'#2563eb',maxWidth:'200px'}}/>
            </div>

            <div className="input-group" style={{marginBottom:'1.25rem'}}>
              <label>Logo URL</label>
              <input className="input-field" placeholder="https://cdn.acme.com/logo.png"
                value={companyForm.logo_url}
                onChange={e=>setCompanyForm(f=>({...f,logo_url:e.target.value}))}
              />
              {companyForm.logo_url && (
                <div style={{marginTop:'0.75rem',display:'flex',alignItems:'center',gap:'0.75rem'}}>
                  <img src={companyForm.logo_url} alt="Logo preview"
                    style={{height:'52px',maxWidth:'180px',objectFit:'contain',borderRadius:'8px',border:'1px solid var(--border)',padding:'4px',background:'var(--surface)'}}
                    onError={e=>{e.target.style.display='none'}}
                  />
                  <span style={{fontSize:'0.76rem',color:'var(--muted)'}}>Preview</span>
                </div>
              )}
            </div>

            <div className="input-group" style={{marginBottom:'1.75rem'}}>
              <label>Favicon URL</label>
              <input className="input-field" placeholder="https://cdn.acme.com/favicon.ico"
                value={companyForm.favicon_url}
                onChange={e=>setCompanyForm(f=>({...f,favicon_url:e.target.value}))}
              />
              {companyForm.favicon_url && (
                <div style={{marginTop:'0.75rem',display:'flex',alignItems:'center',gap:'0.75rem'}}>
                  <img src={companyForm.favicon_url} alt="Favicon preview"
                    style={{height:'32px',width:'32px',objectFit:'contain',borderRadius:'6px',border:'1px solid var(--border)',padding:'3px',background:'var(--surface)'}}
                    onError={e=>{e.target.style.display='none'}}
                  />
                  <span style={{fontSize:'0.76rem',color:'var(--muted)'}}>Preview</span>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isBusy('save-company')}>
                {isBusy('save-company')
                  ? <><span className="btn-spinner"/>Saving…</>
                  : '💾 Save Company Settings'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
